// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/RequisicaoForm.tsx
//  Formulário / Detalhe de Requisição de Materiais
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, ClipboardList, CheckCircle2, PackageCheck,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { requisicaoService }          from '@/features/operacional/services/requisicaoService';
import { calcularStatusRequisicao }   from '@/features/operacional/types';
import { getApiErrorMessage }         from '@/lib/apiClient';
import { ProdutoSearchCombobox }      from '@/components/ProdutoSearchCombobox';

import { Button }    from '@/components/ui/button';
import { Input }     from '@/components/ui/input';
import { Label }     from '@/components/ui/label';
import { Textarea }  from '@/components/ui/textarea';
import { Badge }     from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

import type {
  CriarRequisicaoRequest,
  RequisicaoItemRequest,
  AtualizarRequisicaoRequest,
} from '@/features/operacional/types';

// ── NovaRequisicaoForm ────────────────────────────────────────────────────

function NovaRequisicaoForm() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const hoje = new Date().toISOString().slice(0, 10);
  const [dtMovimento,   setDtMovimento]   = useState(hoje);
  const [dtAgendamento, setDtAgendamento] = useState(hoje);
  const [horaAgend,     setHoraAgend]     = useState('');
  const [setor,         setSetor]         = useState('');
  const [observacao,    setObservacao]    = useState('');
  const [itens, setItens] = useState<(RequisicaoItemRequest & { _key: number })[]>([]);
  const [_nextKey, setNextKey] = useState(1);

  const addItem = () => setNextKey((k) => { setItens((p) => [...p, { _key: k, subProdutoId: 0, quantidade: 1 }]); return k + 1; });
  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));
  const updateItem = (key: number, field: string, value: string) =>
    setItens((p) => p.map((r) => r._key === key
      ? { ...r, [field]: field === 'quantidade' ? Number(value) : value }
      : r));
  const setItemProduto = (key: number, subProdutoId: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, subProdutoId } : r));

  const saveMutation = useMutation({
    mutationFn: () => {
      if (itens.length === 0) throw new Error('Adicione ao menos um item');
      const request: CriarRequisicaoRequest = {
        dtMovimento:   new Date(dtMovimento).toISOString(),
        dtAgendamento: new Date(dtAgendamento).toISOString(),
        horaAgendamento: horaAgend || undefined,
        setorSolicitacaoId: setor ? Number(setor) : 0,
        observacao:    observacao || undefined,
        itens: itens.map(({ subProdutoId, quantidade, motivo }) => ({ subProdutoId, quantidade, motivo })),
      };
      return requisicaoService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requisicao'] });
      toast.success('Requisição criada com sucesso');
      navigate(`/operacional/requisicoes/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/requisicoes')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Nova Requisição de Materiais</h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Identificação</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Data do Movimento</Label>
            <Input type="date" value={dtMovimento} onChange={(e) => setDtMovimento(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Data de Agendamento</Label>
            <Input type="date" value={dtAgendamento} onChange={(e) => setDtAgendamento(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Hora Agendamento</Label>
            <Input type="time" value={horaAgend} onChange={(e) => setHoraAgend(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Setor Solicitante (ID)</Label>
            <Input type="number" value={setor} onChange={(e) => setSetor(e.target.value)} placeholder="ID do setor" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Observação</Label>
            <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)}
              placeholder="Motivo / justificativa da requisição..." rows={3} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Materiais Solicitados</CardTitle>
            <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
              <Plus className="h-4 w-4" />Adicionar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {itens.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhum material adicionado</p>
              <Button variant="outline" className="mt-3 gap-2" onClick={addItem}>
                <Plus className="h-4 w-4" />Adicionar material
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px]">Produto / Material</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead>Motivo / Observação</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((i) => (
                  <TableRow key={i._key}>
                    <TableCell>
                      <ProdutoSearchCombobox
                        value={i.subProdutoId || undefined}
                        onChange={(id) => setItemProduto(i._key, id)}
                        placeholder="Buscar produto..."
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input type="number" value={i.quantidade} className="w-24 text-right"
                        onChange={(e) => updateItem(i._key, 'quantidade', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Input value={i.motivo ?? ''} placeholder="Motivo..."
                        onChange={(e) => updateItem(i._key, 'motivo', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(i._key)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/operacional/requisicoes')}>Cancelar</Button>
        <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4" />Criar Requisição
        </Button>
      </div>
    </div>
  );
}

// ── RequisicaoDetalheView ─────────────────────────────────────────────────

function RequisicaoDetalheView() {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: req, isLoading } = useQuery({
    queryKey: ['requisicao-detalhe', id],
    queryFn: () => requisicaoService.getById(Number(id)),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: AtualizarRequisicaoRequest) => requisicaoService.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-detalhe', id] });
      queryClient.invalidateQueries({ queryKey: ['requisicao'] });
      toast.success('Requisição atualizada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  if (isLoading) return <div className="p-6"><div className="text-center py-12 text-muted-foreground">Carregando...</div></div>;
  if (!req) return null;

  const status = calcularStatusRequisicao(req);

  const badgeClass: Record<string, string> = {
    pendente:  'bg-blue-500/10 text-blue-500',
    aprovada:  'bg-orange-500/10 text-orange-500',
    realizada: 'bg-green-500/10 text-green-500',
  };
  const badgeLabel: Record<string, string> = {
    pendente: 'Pendente', aprovada: 'Aprovada', realizada: 'Realizada',
  };

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/requisicoes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Requisição #{req.id}</h1>
            {req.observacao && <p className="text-muted-foreground text-sm mt-0.5">{req.observacao}</p>}
          </div>
          <Badge className={badgeClass[status]}>{badgeLabel[status]}</Badge>
        </div>
        <div className="flex gap-2">
          {status === 'pendente' && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ isAprovada: true })}>
              <CheckCircle2 className="h-4 w-4" />Aprovar
            </Button>
          )}
          {status === 'aprovada' && (
            <Button className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => updateMutation.mutate({ isRealizada: true })}
              disabled={updateMutation.isPending}>
              <PackageCheck className="h-4 w-4" />Marcar Realizada
            </Button>
          )}
        </div>
      </div>

      {/* Info */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-muted-foreground">Data Movimento</p>
              <p className="font-medium">{new Date(req.dtMovimento).toLocaleDateString('pt-BR')}</p></div>
            <div><p className="text-muted-foreground">Agendamento</p>
              <p className="font-medium">{new Date(req.dtAgendamento).toLocaleDateString('pt-BR')}
                {req.horaAgendamento && <span className="ml-1 text-muted-foreground">{req.horaAgendamento}</span>}
              </p></div>
            <div><p className="text-muted-foreground">Setor</p>
              <p className="font-medium">{req.setorSolicitacaoId > 0 ? `Setor ${req.setorSolicitacaoId}` : '—'}</p></div>
            <div><p className="text-muted-foreground">Itens</p>
              <p className="font-medium">{req.itens?.length ?? 0}</p></div>
          </div>
          {req.observacao && (
            <>
              <Separator className="my-4" />
              <p className="text-sm text-muted-foreground">{req.observacao}</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-5 w-5" />Materiais Solicitados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!req.itens || req.itens.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nenhum material nesta requisição</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sub-Produto ID</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Efetivado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {req.itens.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.subProdutoId}</TableCell>
                    <TableCell className="text-right">{item.quantidade}</TableCell>
                    <TableCell>{item.motivo || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      {item.isEfetivado
                        ? <Badge className="bg-green-500/10 text-green-500">Sim</Badge>
                        : <Badge className="bg-muted text-muted-foreground">Não</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────

const RequisicaoForm = () => {
  const { id } = useParams<{ id: string }>();
  return id && id !== 'nova' ? <RequisicaoDetalheView /> : <NovaRequisicaoForm />;
};

export default RequisicaoForm;
